import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { sendCollectionReminder } from '../../../services/loanService';
import type { CollectionReminder } from '../../../services/schemas/loanServiceSchemas/collectionReminder';
import { formatCurrency, formatDate } from '../../../utils/formatters';

export interface ReminderTarget {
  /** The loan's ledger account id */
  accountId: string;
  accountNumber?: string;
  customerName?: string;
}

interface Props {
  target: ReminderTarget | null;
  onHide: () => void;
  /** Called after a reminder is sent, so the page can refresh what it shows about the loan. */
  onSent?: (sent: CollectionReminder) => void;
}

/** What the client was sent, in the words the follow-up history uses. */
export const describeReminder = (sent: CollectionReminder): string => {
  const by = sent.channel === 'SMS' ? 'SMS' : 'email';
  if (sent.kind === 'OVERDUE_NOTICE') {
    return `Overdue notice sent by ${by}: ${formatCurrency(sent.amount || 0)} payable, ${sent.daysOverdue} days overdue.`;
  }
  return `Payment reminder sent by ${by}: ${formatCurrency(sent.amount || 0)} due ${formatDate(sent.dueDate)}.`;
};

/**
 * Send the client a reminder now, by SMS or email, to the contact details on their customer record. The system
 * picks the message from the loan: an overdue notice for everything payable when an instalment is late,
 * otherwise a reminder of the next instalment. It is logged on the loan's follow-up history.
 */
const SendReminderModal: React.FC<Props> = ({ target, onHide, onSent }) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const [channel, setChannel] = useState<'SMS' | 'EMAIL'>('SMS');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (target) {
      setChannel('SMS');
    }
  }, [target]);

  const send = async () => {
    if (!target) return;
    setIsSending(true);
    try {
      const sent = await sendCollectionReminder(target.accountId, {
        channel,
        sentBy: user?.name || user?.username || undefined,
      });
      toast.success(describeReminder(sent));
      onSent?.(sent);
      onHide();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message || 'The reminder could not be sent');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Modal show={!!target} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa fa-bell me-2 text-primary"></i>
          Send reminder
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-3">
          <strong>{target?.customerName || 'Client'}</strong>
          {target?.accountNumber && <span className="text-muted ms-2">{target.accountNumber}</span>}
        </p>
        <div className="btn-group w-100 mb-3" role="group" aria-label="Send by">
          <button type="button" className={`btn ${channel === 'SMS' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setChannel('SMS')} disabled={isSending}>
            <i className="fa fa-mobile me-2"></i>SMS
          </button>
          <button type="button" className={`btn ${channel === 'EMAIL' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setChannel('EMAIL')} disabled={isSending}>
            <i className="fa fa-envelope me-2"></i>Email
          </button>
        </div>
        <div className="small text-muted">
          <p className="mb-2">
            The message is chosen from the loan: if an instalment is already late, an <strong>overdue notice</strong> for
            everything payable now; otherwise a <strong>reminder of the next instalment</strong> and its due date.
          </p>
          <p className="mb-0">
            It goes to the {channel === 'SMS' ? 'cell number' : 'email address'} on the customer record and is
            logged on the loan's follow-up history. To record a phone call instead, use Log Contact / Promise.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={isSending}>Cancel</Button>
        <Button variant="primary" onClick={send} disabled={isSending || !target}>
          {isSending ? (
            <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Sending...</>
          ) : (
            <><i className="fa fa-paper-plane me-2"></i>Send {channel === 'SMS' ? 'SMS' : 'email'}</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SendReminderModal;
